import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const day = (d) => {
  if (!d) return "";
  const M = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const [, m, dd] = String(d).slice(0, 10).split("-");
  return `${Number(dd)} ${M[Number(m) - 1]}`;
};

// What the month will pay, shown before anyone runs it. A wrong effective
// date is far cheaper to spot here than on a payslip someone has been sent.
export default function StaffPayrollPreview({ month, refreshKey }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/staffing/payroll-preview", { params: { month } });
        if (alive) setData(data);
      } catch (e) {
        if (alive) { setData(null); toast.error(formatApiError(e)); }
      }
    })();
    return () => { alive = false; };
  }, [month, refreshKey]);

  if (!data || !data.rows || !data.rows.length) return null;
  const flagged = data.rows.filter((r) => r.rate_changed_mid_month || r.partial);

  return (
    <div className="rounded-lg border border-zinc-200" data-testid="payroll-preview">
      <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-zinc-100">
        <div className="text-sm font-semibold">This month</div>
        <div className="text-[11px] text-zinc-500">
          {data.rows.length} active &middot; {inr(data.total_if_full)} at full month
          {data.already_run > 0 && ` · ${data.already_run} already run`}
        </div>
      </div>

      {!flagged.length ? (
        <div className="px-3 py-3 text-xs text-zinc-500">
          Nothing needs attention — no rate changes or part months.
        </div>
      ) : (
        <div className="divide-y divide-zinc-100">
          {flagged.map((r) => (
            <div key={r.staff_id} className="px-3 py-2.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium">{r.name}</span>
                {r.rate_changed_mid_month && (
                  <Badge variant="outline"
                         className="text-[10px] border-amber-300 text-amber-800 bg-amber-50">
                    rate changes mid-month
                  </Badge>
                )}
                {r.partial && (
                  <Badge variant="outline"
                         className="text-[10px] border-amber-300 text-amber-800 bg-amber-50">
                    {String(r.doj || "").slice(0, 7) === month
                      ? `joined ${day(r.doj)}`
                      : `last day ${day(r.last_working_day)}`}
                  </Badge>
                )}
                {r.already_generated && (
                  <Badge variant="outline" className="text-[10px]">
                    already run &middot; {inr(r.generated_gross)}
                    {r.by_hand ? " (by hand)" : ""}
                  </Badge>
                )}
              </div>

              {r.rate_changed_mid_month && (
                <div className="mt-1 space-y-0.5">
                  {r.segments.map((s, i) => (
                    <div key={i} className="flex justify-between text-xs text-zinc-600">
                      <span>{day(s.from)} &ndash; {day(s.to)} &middot; {s.days}d @ {inr(s.rate)}</span>
                      <span>{inr(s.amount)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-xs font-medium">
                    <span>Month total</span><span>{inr(r.monthly_amount)}</span>
                  </div>
                </div>
              )}

              {r.partial && !r.rate_changed_mid_month && (
                <div className="text-[11px] text-zinc-500 mt-0.5">
                  Needs a day count, or an amount typed in.
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
